/* =====================================================================
 * App — 루트 컴포넌트 (BrowserRouter + AppRouter)
 * ===================================================================== */

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from 'styled-components';
import { createTheme, type TenantThemeOverride } from '@/theme/theme';
import { GlobalStyle } from '@/theme/GlobalStyle';
import { ToastContainer } from '@auw/workspace-ui';
import { TokenExhaustedModal } from '@auw/workspace-ui';
import { GA4Tracker } from '@/monitoring/GA4Tracker';
import { GlobalStructuredData } from '@/routes/StructuredData';
import { AppRouter } from '@/routes/AppRouter';
import { useUIStore } from '@auw/stores';
import { useUserStore, restoreUserCache } from '@auw/stores';
import { useTeamStore } from '@auw/stores';
import { hasAuthToken, acceptTeamInviteLink, getBillingStatus, exchangeAuthCodeForDesktop } from '@auw/api-client';
import { clearAuthTokens, ensureFreshToken, setAuthTokens, storeDesktopRefreshToken } from '@auw/api-client';
import { isDesktop } from '@auw/workspace-utils';
import {
  bootstrapActiveAccountId,
  getActiveAuthAccount,
  isAutoRestoreSuppressedForTab,
  startActiveAccountInvalidationGuard,
  startFocusedAccountTracking,
} from '@auw/api-client';
import { queryKeys } from '@auw/workspace-utils';
import { clearQuerySessionStorage } from '@auw/workspace-utils';
import { prefetchChatRenderModules } from '@auw/workspace-ui';
import {
  subscribeAuthSessionInvalidated,
  type AuthSessionInvalidatedDetail,
} from '@auw/workspace-utils';
import {
  markAuthReady,
  markFirstInteractiveWorkspace,
  markLoginStart,
  markWorkspaceBootstrapDone,
  markWorkspaceBootstrapStart,
} from '@auw/workspace-utils';
import { resetRuntimeSessionState } from '@auw/stores';
import {
  clearPluginOAuthCallbackParams,
  isLikelyPluginOAuthPopupWindow,
  publishPluginOAuthResult,
  readPluginOAuthCallbackFromLocation,
} from '@auw/workspace-utils';
import { useTranslation } from 'react-i18next';
import { getUserLocale } from '@auw/workspace-utils';
import { useAppLocale } from '@auw/i18n';
import { useFeatureFlags } from '@auw/feature-flags';
import { useTenant } from '@auw/tenant';
import { modeToPath } from '@auw/workspace-utils';

function isPageForeground(): boolean {
  if (typeof document === 'undefined') return true;
  const prerenderingDocument = document as Document & { prerendering?: boolean };
  return prerenderingDocument.prerendering !== true && document.visibilityState === 'visible';
}

/**
 * Skip a forced redirect when the browser is already sitting on the
 * tenant's own "workspace landing" surfaces. The caller supplies the
 * current agent-mode URL because tenants can rebrand it (`/agents` on
 * B2C, `modeToPath('agent')` resolves to whatever the tenant's agent
 * route entry is, or the tenant's `workspace.defaultPath` fallback if
 * agent mode is disabled entirely).
 */
function shouldRedirectToWorkspaceLanding(
  pathname: string,
  agentPath: string,
): boolean {
  return pathname !== '/'
    && pathname !== agentPath
    && pathname !== '/auth/callback'
    && !pathname.startsWith('/share/');
}

const MARKETING_SILENT_REFRESH_SKIP_PATHS = new Set([
  '/pricing',
  '/terms',
  '/privacy',
  '/contact-sales',
  '/marketing-consent',
]);

function shouldAttemptSilentRefreshOnEntry(pathname: string): boolean {
  return !MARKETING_SILENT_REFRESH_SKIP_PATHS.has(pathname);
}

const App: React.FC = () => {
  const queryClient = useQueryClient();
  const darkMode = useUIStore((s) => s.darkMode);
  const addToast = useUIStore((s) => s.addToast);
  const tokenExhaustedModalOpen = useUIStore((s) => s.tokenExhaustedModalOpen);
  const tokenExhaustedTrialEndsAt = useUIStore((s) => s.tokenExhaustedTrialEndsAt);
  const closeTokenExhaustedModal = useUIStore((s) => s.closeTokenExhaustedModal);
  const syncWallpaperForActiveAccount = useUIStore((s) => s.syncWallpaperForActiveAccount);
  const hydrateWallpaperFromCloud = useUIStore((s) => s.hydrateWallpaperFromCloud);
  const flushWallpaperToCloud = useUIStore((s) => s.flushWallpaperToCloud);
  const syncDarkModeForActiveAccount = useUIStore((s) => s.syncDarkModeForActiveAccount);
  const hydrateThemeFromCloud = useUIStore((s) => s.hydrateThemeFromCloud);
  const flushThemeToCloud = useUIStore((s) => s.flushThemeToCloud);
  /* Phase 11-4c: `useAppLocale` lives in `@auw/i18n` and no longer
   * reaches into this host's userStore. The host is responsible for
   * selecting the preferred language (B2C: userStore) and passing it
   * in. Enterprise tenants will feed their own profile slice in when
   * the admin shell starts calling `useAppLocale` (P11-6). */
  const preferredLanguage = useUserStore((s) => s.user?.preferredLanguage);
  useAppLocale(preferredLanguage);
  const { t } = useTranslation(['common', 'auth']);
  const { isEnabled: isFeatureEnabled } = useFeatureFlags();
  const tenant = useTenant();
  /* `modeToPath('agent')` resolves to the tenant's agent route entry
   * (B2C: `/agents`) or the tenant's landing path when agent mode is
   * disabled. Every legacy `'/agents'` string below now routes through
   * this value so on-prem tenants can remap the surface without
   * touching app code. */
  const agentLandingPath = modeToPath('agent');
  // Plugin OAuth popups can only originate from a tenant that exposes
  // plugins at all — either as a workspace mode or as the standalone
  // settings page. For tenants with both flags off the callback is
  // impossible to reach through the UI, so skip the global handler to
  // avoid any accidental `window.close()` on shared-URL hits.
  const pluginsEnabled =
    isFeatureEnabled('mode.plugin') || isFeatureEnabled('page.plugin');
  /**
   * Theme is derived per-render from `darkMode` (UI store) and the
   * active tenant's optional palette override. The cast is safe
   * because `TenantTheme`'s zod-validated shape is structurally a
   * subset of `TenantThemeOverride` — both expose `light?`/`dark?`
   * blocks of partial palette groups, and `mergePalette` silently
   * drops slot ids that aren't part of the canonical palette.
   *
   * `useMemo` keeps the produced AppTheme reference stable across
   * unrelated re-renders so styled-components doesn't invalidate
   * every cached style block.
   */
  const theme = useMemo(
    () =>
      createTheme(
        darkMode ? 'dark' : 'light',
        tenant.theme as TenantThemeOverride | undefined,
      ),
    [darkMode, tenant.theme],
  );
  const [authChecking, setAuthChecking] = useState(true);
  const [isAuthed, setIsAuthed] = useState(false);
  const [handledInviteToken, setHandledInviteToken] = useState('');
  const [pageVisible, setPageVisible] = useState(() => isPageForeground());
  const loginPerfStartedRef = useRef(false);
  const authReadyMarkedRef = useRef(false);
  const workspaceBootstrapStartedRef = useRef(false);
  const firstWorkspaceInteractiveRef = useRef(false);

  const path = window.location.pathname;
  const search = window.location.search;
  const isSharePage = path.startsWith('/share/');
  const invitePathParts = path.split('/').filter(Boolean);
  const inviteTokenFromPath =
    invitePathParts[0] === 'invite' && invitePathParts[1] === 'team' && invitePathParts[2]
      ? decodeURIComponent(invitePathParts[2])
      : '';
  const inviteTokenFromQuery = new URLSearchParams(search).get('teamInvite') || '';
  const inviteToken = inviteTokenFromPath || inviteTokenFromQuery;

  const loadUser = useUserStore((s) => s.loadUser);
  const updateProfile = useUserStore((s) => s.updateProfile);
  const user = useUserStore((s) => s.user);
  const userId = useUserStore((s) => s.user?.userId || '');
  const loadMyTeams = useTeamStore((s) => s.loadMyTeams);
  const setWorkspaceView = useTeamStore((s) => s.setWorkspaceView);

  const markAuthReadyOnce = useCallback((source: 'optimistic' | 'validated' | 'callback') => {
    if (authReadyMarkedRef.current) return;
    authReadyMarkedRef.current = true;
    markAuthReady({ source });
  }, []);

  const finalizeAuthenticatedState = useCallback((source: 'optimistic' | 'validated' | 'callback') => {
    setIsAuthed(true);
    setAuthChecking(false);
    markAuthReadyOnce(source);
  }, [markAuthReadyOnce]);

  const triggerWorkspaceBootstrap = useCallback(() => {
    if (!workspaceBootstrapStartedRef.current) {
      workspaceBootstrapStartedRef.current = true;
      markWorkspaceBootstrapStart();
    }
    import('@auw/stores').then(({ useChatStore }) => {
      void useChatStore.getState().loadFromBackendApi().finally(() => {
        if (firstWorkspaceInteractiveRef.current) return;
        firstWorkspaceInteractiveRef.current = true;
        markWorkspaceBootstrapDone();
        markFirstInteractiveWorkspace();
      });
    });
  }, []);

  const invalidateAuthSession = useCallback((detail?: AuthSessionInvalidatedDetail) => {
    resetRuntimeSessionState();
    queryClient.clear();
    clearQuerySessionStorage();
    setIsAuthed(false);
    setAuthChecking(false);

    const currentPath = window.location.pathname;
    const redirectTarget = detail?.redirectTo ?? agentLandingPath;
    if (redirectTarget && shouldRedirectToWorkspaceLanding(currentPath, agentLandingPath)) {
      window.location.href = redirectTarget;
    }
  }, [agentLandingPath, queryClient]);

  const triggerWorkspaceBootstrapIfAllowed = useCallback((candidateUser?: {
    onboardingCompleted: boolean;
  } | null) => {
    const resolvedUser = candidateUser ?? useUserStore.getState().user;
    if (!resolvedUser || resolvedUser.onboardingCompleted === false) return;
    triggerWorkspaceBootstrap();
  }, [triggerWorkspaceBootstrap]);

  // 전역 OAuth 콜백 처리:
  // 팝업 탭에서 돌아왔을 때 route와 무관하게(예: /agents) 부모 탭에 결과를 전달하고 즉시 닫는다.
  // On-prem tenants that disable both plugin surfaces skip this entirely —
  // the BroadcastChannel/storage bridge would otherwise fan out even
  // though no plugin modal is ever mounted to consume the message.
  useEffect(() => {
    if (!pluginsEnabled) return;
    const callback = readPluginOAuthCallbackFromLocation();
    if (!callback) return;
    if (!callback.popupExpected && !isLikelyPluginOAuthPopupWindow()) return;

    publishPluginOAuthResult(callback);
    clearPluginOAuthCallbackParams();
    window.close();
    const retry1 = window.setTimeout(() => window.close(), 120);
    const retry2 = window.setTimeout(() => window.close(), 320);
    return () => {
      window.clearTimeout(retry1);
      window.clearTimeout(retry2);
    };
  }, [pluginsEnabled]);

  useEffect(() => {
    const stopTracking = startFocusedAccountTracking();
    return () => {
      stopTracking();
    };
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return () => {};
    const syncVisibility = () => {
      setPageVisible(isPageForeground());
    };
    document.addEventListener('visibilitychange', syncVisibility);
    document.addEventListener('prerenderingchange', syncVisibility);
    window.addEventListener('pageshow', syncVisibility);
    syncVisibility();
    return () => {
      document.removeEventListener('visibilitychange', syncVisibility);
      document.removeEventListener('prerenderingchange', syncVisibility);
      window.removeEventListener('pageshow', syncVisibility);
    };
  }, []);

  useEffect(() => {
    if (isSharePage || authChecking) return;
    if (hasAuthToken() || isAuthed) {
      import('@/components/layout/Layout');
    }
  }, [isSharePage, authChecking, isAuthed]);

  useEffect(() => {
    if (isSharePage || !isAuthed || !user) return;
    triggerWorkspaceBootstrapIfAllowed(user);
  }, [isSharePage, isAuthed, user, triggerWorkspaceBootstrapIfAllowed]);

  useEffect(() => {
    if (isSharePage || !isAuthed) return;

    let cancelled = false;
    const runPrefetch = () => {
      if (!cancelled) prefetchChatRenderModules();
    };

    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(runPrefetch, { timeout: 2_500 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback(idleId);
      };
    }

    const timer = window.setTimeout(runPrefetch, 1_200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [isSharePage, isAuthed]);

  useEffect(() => {
    if (!isAuthed || !user?.userId) return;
    const timezone = getUserLocale().timezone || 'Asia/Seoul';
    /* Tenant-prefixed key (Phase 11-3a) keeps B2C and any future
     * enterprise deployment that shares the browser origin from
     * stomping on each other's last-synced timezone. B2C keeps the
     * same value on-disk because `tenant.id === 'askyourai'`. */
    const syncKey = `${tenant.id}_timezone_sync:${user.userId}`;
    if (!timezone || user.timezone === timezone) {
      try {
        sessionStorage.setItem(syncKey, timezone);
      } catch {
        // ignore
      }
      return;
    }

    let lastSynced = '';
    try {
      lastSynced = sessionStorage.getItem(syncKey) || '';
    } catch {
      // ignore
    }
    if (lastSynced === timezone) return;

    try {
      sessionStorage.setItem(syncKey, timezone);
    } catch {
      // ignore
    }

    void updateProfile({ timezone }).catch(() => {
      try {
        sessionStorage.removeItem(syncKey);
      } catch {
        // ignore
      }
    });
  }, [isAuthed, tenant.id, updateProfile, user?.timezone, user?.userId]);

  useEffect(() => {
    const stopGuard = startActiveAccountInvalidationGuard(() => {
      invalidateAuthSession();
    });
    const stopSameTabInvalidation = subscribeAuthSessionInvalidated((detail) => {
      invalidateAuthSession(detail);
    });

    return () => {
      stopGuard();
      stopSameTabInvalidation();
    };
  }, [invalidateAuthSession]);

  useEffect(() => {
    if (!pageVisible) {
      const HIDDEN_FAILSAFE_MS = 30_000;
      const hiddenFailsafeId = window.setTimeout(() => {
        if (!isPageForeground()) return;
        setPageVisible(true);
      }, HIDDEN_FAILSAFE_MS);
      return () => {
        window.clearTimeout(hiddenFailsafeId);
      };
    }
    if (isSharePage) {
      setIsAuthed(false);
      setAuthChecking(false);
      return;
    }

    let didOptimistic = false;
    let settled = false;

    const check = async () => {
      try {
        if (!loginPerfStartedRef.current) {
          loginPerfStartedRef.current = true;
          markLoginStart();
        }
        let candidateAccountId = bootstrapActiveAccountId();
        const shouldSkipSilentRefresh = isAutoRestoreSuppressedForTab();
        const shouldAttemptSilentRefresh = shouldAttemptSilentRefreshOnEntry(path);

        if (settled) return;

        // 공개 랜딩에서는 첫 페인트 전에 쿠키 세션 복구를 강제하지 않는다.
        if (
          !candidateAccountId
          && !hasAuthToken()
          && !shouldSkipSilentRefresh
          && shouldAttemptSilentRefresh
        ) {
          let restoredToken = await ensureFreshToken();
          if (!restoredToken) {
            await new Promise<void>((resolve) => {
              window.setTimeout(resolve, 350);
            });
            if (settled) return;
            restoredToken = await ensureFreshToken();
          }
          if (settled) return;
          if (restoredToken) {
            candidateAccountId = bootstrapActiveAccountId();
          }
        }

        if (!candidateAccountId && !hasAuthToken()) {
          if (!settled) {
            settled = true;
            setIsAuthed(false);
            setAuthChecking(false);
          }
          return;
        }

        /* ── 낙관적 인증: 캐시된 유저가 있으면 즉시 Layout 렌더 ── */
        if (candidateAccountId) {
          const account = getActiveAuthAccount();
          const cached = account
            ? restoreUserCache(account.userId)
            : null;

          if (cached) {
            useUserStore.setState({
              user: cached.user,
              usage: cached.usage,
              isLoaded: true,
            });
            didOptimistic = true;
            finalizeAuthenticatedState('optimistic');
            triggerWorkspaceBootstrapIfAllowed(cached.user);
          }
        }

        /* ── 서버 검증 (낙관적이든 아니든 반드시 실행) ── */
        while (candidateAccountId || hasAuthToken()) {
          if (settled) return;
          await ensureFreshToken();
          if (settled) return;
          await loadUser();
          if (settled) return;
          const user = useUserStore.getState().user;
          if (user) {
            if (!didOptimistic) {
              settled = true;
              finalizeAuthenticatedState('validated');
            }
            triggerWorkspaceBootstrapIfAllowed(user);
            return;
          }

          clearAuthTokens();
          candidateAccountId = bootstrapActiveAccountId();
        }

        /* 모든 계정 실패 → 낙관적 인증 되돌리기 (단, 일시 오류로 유저가 남아있으면 유지) */
        if (!settled && !useUserStore.getState().user) {
          settled = true;
          setIsAuthed(false);
          setAuthChecking(false);
        }
      } catch {
        if (!settled && !useUserStore.getState().user) {
          settled = true;
          setIsAuthed(false);
          setAuthChecking(false);
        }
      }
    };

    const AUTH_CHECK_FAILSAFE_MS = 15_000;
    const failsafeId = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      if (!useUserStore.getState().user) {
        setIsAuthed(false);
        setAuthChecking(false);
      }
    }, AUTH_CHECK_FAILSAFE_MS);

    void check().finally(() => {
      window.clearTimeout(failsafeId);
    });

    return () => {
      settled = true;
      window.clearTimeout(failsafeId);
    };
  }, [finalizeAuthenticatedState, isSharePage, loadUser, pageVisible, triggerWorkspaceBootstrapIfAllowed]);

  useEffect(() => {
    if (isSharePage || !isAuthed) return;

    let active = true;
    const syncPlanFromBilling = async () => {
      const retryDelays = [0, 3000, 7000];
      for (let i = 0; i < retryDelays.length; i++) {
        if (!active) return;
        if (i > 0) {
          await new Promise<void>((resolve) => {
            window.setTimeout(resolve, retryDelays[i]);
          });
          if (!active) return;
        }
        try {
          const status = await queryClient.fetchQuery({
            queryKey: queryKeys.billing.status(),
            queryFn: getBillingStatus,
            staleTime: 0,
          });
          const remotePlan = String(status.subscription_plan || '').trim().toLowerCase();
          const localPlan = String(useUserStore.getState().user?.plan || '').trim().toLowerCase();
          if (remotePlan && remotePlan !== localPlan) {
            await loadUser();
          }
          // fetch 성공 시에는 즉시 종료하고, 실패 시에만 retry 루프를 진행한다.
          return;
        } catch {
          // retry loop continues
        }
      }
    };

    const timer = window.setTimeout(() => {
      if (!active) return;
      void syncPlanFromBilling();
    }, 1_500);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [isSharePage, isAuthed, loadUser, queryClient]);

  useEffect(() => {
    if (isSharePage) return;
    syncWallpaperForActiveAccount();
  }, [isSharePage, isAuthed, userId, syncWallpaperForActiveAccount]);

  useEffect(() => {
    if (isSharePage || !isAuthed || !userId) return;
    void hydrateWallpaperFromCloud();
  }, [isSharePage, isAuthed, userId, hydrateWallpaperFromCloud]);

  useEffect(() => {
    if (isSharePage || !isAuthed) return;
    const flush = () => {
      void flushWallpaperToCloud();
    };
    window.addEventListener('beforeunload', flush);
    return () => window.removeEventListener('beforeunload', flush);
  }, [isSharePage, isAuthed, flushWallpaperToCloud]);

  // 테마(다크/라이트)도 wallpaper 와 동일하게 계정 단위로 동기화한다.
  // 계정이 바뀌면 로컬 스코프 재동기화 → 서버에 저장된 유저별 테마로 hydrate.
  useEffect(() => {
    if (isSharePage) return;
    syncDarkModeForActiveAccount();
  }, [isSharePage, isAuthed, userId, syncDarkModeForActiveAccount]);

  useEffect(() => {
    if (isSharePage || !isAuthed || !userId) return;
    void hydrateThemeFromCloud();
  }, [isSharePage, isAuthed, userId, hydrateThemeFromCloud]);

  useEffect(() => {
    if (isSharePage || !isAuthed) return;
    const flush = () => {
      void flushThemeToCloud({ keepalive: true });
    };
    window.addEventListener('beforeunload', flush);
    return () => window.removeEventListener('beforeunload', flush);
  }, [isSharePage, isAuthed, flushThemeToCloud]);

  useEffect(() => {
    if (!isAuthed || !inviteToken || handledInviteToken === inviteToken) return;
    let active = true;

    const run = async () => {
      try {
        const result = await acceptTeamInviteLink(inviteToken);
        await loadMyTeams();
        setWorkspaceView(result.team_id);
        addToast({
          type: 'success',
          title: result.already_member ? t('auth:invite.alreadyMember') : t('auth:invite.teamJoined'),
          description: t('auth:invite.teamConnected', { teamName: result.team_name }),
        });
      } catch (err) {
        addToast({
          type: 'error',
          title: t('auth:invite.joinFail'),
          description: err instanceof Error ? err.message : '',
        });
      } finally {
        if (!active) return;
        setHandledInviteToken(inviteToken);
        if (window.location.pathname.startsWith('/invite/team/')) {
          window.history.replaceState({}, '', agentLandingPath);
        } else {
          const params = new URLSearchParams(window.location.search);
          params.delete('teamInvite');
          const qs = params.toString();
          window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
        }
      }
    };
    void run();
    return () => {
      active = false;
    };
  }, [agentLandingPath, isAuthed, inviteToken, handledInviteToken, addToast, loadMyTeams, setWorkspaceView]);

  const handleAuthed = useCallback(async () => {
    finalizeAuthenticatedState('callback');
    try {
      await loadUser();
      const resolvedUser = useUserStore.getState().user;
      if (!resolvedUser) {
        throw new Error(t('common:error.loadUserFail'));
      }
      triggerWorkspaceBootstrapIfAllowed(resolvedUser);
    } catch (error) {
      clearAuthTokens();
      resetRuntimeSessionState();
      queryClient.clear();
      clearQuerySessionStorage();
      setIsAuthed(false);
      setAuthChecking(false);
      throw error;
    }
  }, [finalizeAuthenticatedState, loadUser, queryClient, triggerWorkspaceBootstrapIfAllowed]);

  useEffect(() => {
    if (!isDesktop) return;
    (window as unknown as Record<string, unknown>).__TAURI_DEEP_LINK__ = async (urls: string[]) => {
      try {
        for (const raw of urls) {
          const url = new URL(raw);
          const route = `${url.host}${url.pathname}`.replace(/^\/+/, '');
          if (route !== 'auth/callback') continue;

          const accessToken = url.searchParams.get('access_token');
          const refreshToken = url.searchParams.get('refresh_token');
          const code = url.searchParams.get('code');

          if (code) {
            const result = await exchangeAuthCodeForDesktop(code);
            setAuthTokens(result.access_token);
            await storeDesktopRefreshToken(result.refresh_token);
            void handleAuthed();
            return;
          }

          if (accessToken) {
            setAuthTokens(accessToken);
            if (refreshToken) {
              await storeDesktopRefreshToken(refreshToken);
            }
            void handleAuthed();
            return;
          }
        }
      } catch {
        // deep link auth 실패 시 로그인 페이지 유지
      }
    };
    return () => {
      delete (window as unknown as Record<string, unknown>).__TAURI_DEEP_LINK__;
    };
  }, [handleAuthed]);

  return (
    <HelmetProvider>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <ThemeProvider theme={theme}>
          <GlobalStyle />
          <GA4Tracker />
          <GlobalStructuredData />
          <AppRouter
            isAuthed={isAuthed}
            authChecking={authChecking}
            inviteToken={inviteToken}
            onAuthed={handleAuthed}
          />
          <ToastContainer />
          <TokenExhaustedModal
            open={tokenExhaustedModalOpen}
            onClose={closeTokenExhaustedModal}
            trialEndsAt={tokenExhaustedTrialEndsAt}
          />
        </ThemeProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
};

export default App;
